
"use client";


import { cn } from "@/lib/utils";
import { useEffect, useMemo, useState } from "react";
import { shallow } from "zustand/shallow";
import { useStore, useUser, useUserVip } from "@/stores/appStore";
import { HelpAccordion } from "../site/pricing/HelpAccordion";
import { PriceBadge } from "../site/PriceBadge";
import { BadgeType, type LadderVipPayment, PaymentRightsItem } from "@/stores/slices/commonSlice";
import { MemberLabel } from "@/components/fun-ui/MemberLabel";
import { MemberButton } from "@/components/fun-ui/MemberButton";
import { PaymentRightsList } from "@/components/payment/PaymentRightsList";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UpgradeVipButton } from "@/components/payment/UpgradeVipButton";
import { useRouter } from "next/navigation";
import { CommunicateWithUs } from "@/components/CommunicateWithUs";
import Loading from "../Loading";
import { ladderUserTierFromVip, matchLadderUserTier } from "@/lib/payment";

function sameMonthlyUnitPrice(discounted: number | string, original: number | string): boolean {
  const a = Number(discounted);
  const b = Number(original);
  if (Number.isFinite(a) && Number.isFinite(b)) {
    return Math.abs(a - b) < 1e-6;
  }
  return String(discounted).trim() === String(original).trim();
}


export function PaymentUpgrade({ membershipPlan = 'plus' }: { membershipPlan?: 'plus' | 'plus3x' }) {
  const [activedIndex, setActivedIndex] = useState(0);

  const { value, fetch } = useStore(
    (s) =>
      membershipPlan === 'plus'
        ? { value: s.paymentUpgradeSet.value, fetch: s.paymentUpgradeSet.fetch }
        : { value: s.paymentUpgradeSetPlus3x.value, fetch: s.paymentUpgradeSetPlus3x.fetch },
    shallow,
  );

  useEffect(() => {
    fetch();
  }, [fetch, membershipPlan]);

  const router = useRouter();

  if (!value?.features?.length) {
    return (
      <div className="h-screen w-full">
        <div className="h-[600px] relative w-full"><Loading /></div>
      </div>
    )
  }

  return (
    <>
      <div className="flex flex-col justify-center items-center min-w-[680px] mt-[34px]">
        <h2 className="text-5xl leading-[78px] text-foreground font-semibold">{value?.title}</h2>
      </div>
      <div className="flex flex-row gap-6 justify-center mt-20">
        {
          value?.features?.map(({
            title,
            label,
            desc,
            button,
            rightsList,
            vipTabas,
          }, index) => {
            return (
              <div key={label}
                className={cn(
                  "rounded-xl flex-1 transition-all	duration-500",
                  "hover:m-0 hover:p-1 hover:bg-gradient-to-r hover:from-[#6A5035] hover:to-[#8C7155]",
                  "m-0.5 p-0.5 bg-border"
                )}
                onMouseEnter={() => {
                  setActivedIndex(index);
                }}
              >
                <div className={cn(
                  "relative rounded-lg bg-background py-7 px-8 h-full",
                )}>
                  <div
                    className="text-[#3D3D3D] text-2xl font-semibold"
                  >
                    {label}
                  </div>
                  {
                    !vipTabas ? (
                      <div className="flex flex-col h-[134px] justify-center">
                        {
                          title && (
                            <div className="mt-4 font-semibold">

                              {
                                title.split('\n').map((str) => {
                                  return (
                                    <MemberLabel
                                      key={str}
                                      className="block text-[42px] leading-[50px]"
                                    >
                                      {str}
                                    </MemberLabel>
                                  )
                                })
                              }

                            </div>
                          )
                        }
                        {
                          desc && (
                            <div className="mt-4">{desc}</div>
                          )
                        }
                      </div>
                    ) : (
                      <UpgradeVipList activedIndex={activedIndex} buttonLabel={button?.label} membershipPlan={membershipPlan} />
                    )
                  }
                  {
                    (!vipTabas && button) && (
                      <div className="mt-4">
                        {
                          index === 0 ? (
                            <MemberButton
                              size="3xl"
                              variant={button?.type as 'default' | 'outline'}
                              className="text-[#f0e1cb] min-w-[260px]"
                              onClick={() => {
                                router.push(`/login`);
                              }}
                            >
                              {button?.label}
                            </MemberButton>
                          ) : null
                        }
                        {
                          index === 2 ? (
                            <CommunicateWithUs
                              size={200}

                            >
                              <MemberButton
                                asChild
                                size="3xl"
                                variant={button?.type as 'default' | 'outline'}
                                className="text-[#f0e1cb] min-w-[260px]"
                              >
                                {button?.label}
                              </MemberButton>
                            </CommunicateWithUs>
                          ) : null
                        }
                      </div>
                    )
                  }
                  {
                    !vipTabas && rightsList && (
                      <PaymentRightsList
                        items={rightsList as PaymentRightsItem[]}
                        className="mt-8"
                      />
                    )
                  }
                </div>
              </div>
            )
          })
        }
      </div>
      <HelpAccordion tagKey="plus" />
    </>
  )
}

export type UpgradeVipListProps = {
  activedIndex: number;
  buttonLabel?: string;
  membershipPlan?: 'plus' | 'plus3x';
}

export const UpgradeVipList = ({
  activedIndex: _activedIndex = 0,
  membershipPlan = 'plus',
}: UpgradeVipListProps) => {

  const [activedIndex, setActivedIndex] = useState(_activedIndex);

  const { value, fetch } = useStore(
    (s) =>
      membershipPlan === 'plus'
        ? { value: s.paymentUpgradeSet.value, fetch: s.paymentUpgradeSet.fetch }
        : { value: s.paymentUpgradeSetPlus3x.value, fetch: s.paymentUpgradeSetPlus3x.fetch },
    shallow,
  );
  const {
    vip,
    vipType,
  } = useUserVip();
  const {
    fetchVipUpgradePreview,
    vipUpgradePreview,
    vipUpgradePreviewLoading,
  } = useUser();

  useEffect(() => {
    fetch();
  }, [fetch, membershipPlan]);
  useEffect(() => {
    if (membershipPlan === 'plus3x' && vip === 1 && vipType === 'plus') {
      fetchVipUpgradePreview();
    }
  }, [membershipPlan, vip, vipType, fetchVipUpgradePreview]);

  const vipFeatureRightsList = value?.features?.find((feature) => feature.vipTabas)?.rightsList as PaymentRightsItem[] | undefined;
  const currentUserTier = ladderUserTierFromVip(vip, vipType);
  const usePlus3xPreview = membershipPlan === 'plus3x' && vip === 1 && vipType === 'plus';
  const displayItems = useMemo((): LadderVipPayment[] => {
    const sourceItems = value?.items || [];
    const filteredByUsers = membershipPlan === 'plus3x'
      ? sourceItems.filter((item) => {
        if (!item.users?.length) {
          return true;
        }
        return matchLadderUserTier(item.users, currentUserTier);
      })
      : sourceItems;
    if (!usePlus3xPreview) {
      return filteredByUsers;
    }
    if (vipUpgradePreviewLoading && !vipUpgradePreview) {
      return [];
    }
    const preview = vipUpgradePreview;
    if (!preview?.upgradeOptions?.length) {
      return filteredByUsers;
    }
    return filteredByUsers.filter((item) => {
      const previewOption = preview.upgradeOptions.find(
        (option) => Math.abs(Number(option.price) - Number(item.price)) < 0.02,
      );
      if (!previewOption) return false;
      if (previewOption.actualAmount <= 0) return false;
      if (previewOption.status === 'exceeded') return false;
      return true;
    });
  }, [value?.items, membershipPlan, currentUserTier, usePlus3xPreview, vipUpgradePreviewLoading, vipUpgradePreview]);
  useEffect(() => {
    if (!displayItems.length) return;
    if (activedIndex >= displayItems.length) {
      setActivedIndex(0);
    }
  }, [displayItems.length, activedIndex]);


  return (
    <Tabs
      value={activedIndex.toString()}
      onValueChange={(tab) => {
        setActivedIndex(Number(tab))
      }}
      className="mt-6"
    >
      <TabsList
        className="flex fle-row rounded-md h-[34px] bg-black/5 items-center justify-center self-center px-1.5 min-w-[244px]"
      >
        {
          displayItems.map(({
            label,
          }, index) => (
            <TabsTrigger
              key={index}
              className={cn(
                "data-[state=active]:text-primary data-[state=active]:shadow-none data-[state=active]:rounded-md text-muted-foreground",
                "data-[state=active]:bg-gradient-to-br data-[state=active]:from-[#a08669] data-[state=active]:to-[#553a20] data-[state=active]:text-[#EDDBC0]",
                "h-[26px] w-[80px] text-[14px] leading-none font-normal"
              )}
              value={index.toString()}
            >
              {label}
            </TabsTrigger>
          ))
        }
      </TabsList>
      {
        displayItems.map(({
          price,
          priceDiscountedPerMonth,
          priceOriginalPerMonth,
          desc,
          badge,
        }, index) => {
          const showMonthlyPriceRow = !sameMonthlyUnitPrice(
            priceDiscountedPerMonth,
            priceOriginalPerMonth
          );
          return (
            <TabsContent
              key={index}
              value={index.toString()}
              className=" mt-[26px]"
            >
              <div className={cn(
                "relative rounded-lg bg-background",
              )}>
                <div className="flex flex-row font-semibold pl-1 h-[50px]">
                  <div>
                    <MemberLabel className="text-2xl">¥</MemberLabel>
                    <MemberLabel className="text-5xl leading-none">{price}</MemberLabel>
                  </div>
                  <div className="flex flex-col ml-1">
                    <div className="w-fit">
                      <PriceBadge label={desc} badge={badge as BadgeType} rounded size={12} />
                    </div>
                    {showMonthlyPriceRow ? (
                      <div className="text-annotation-foreground inline-flex gap-2 text-xs mt-1">
                        <span>¥{priceDiscountedPerMonth}/月</span>
                        <span><span className="line-through">¥{priceOriginalPerMonth}</span>/月</span>
                      </div>
                    ) : null}
                  </div>
                </div>
                <UpgradeVipButton
                  size="3xl"
                  withoutIcon={true}
                  className="mt-4 min-w-[260px]"
                  activedPrice={displayItems[activedIndex]?.price || 0}
                  paymentType={membershipPlan === 'plus3x' ? 'upgradePlus3x' : 'upgrade'}
                >
                  {membershipPlan === 'plus3x' ? '升级到 Plus 3倍会员' : '升级到 Plus 会员'}
                </UpgradeVipButton>
                <PaymentRightsList
                  className="mt-8"
                  items={vipFeatureRightsList || []}
                />
              </div>
            </TabsContent>
          )
        })
      }
    </Tabs>
  )
}